import { groupBy } from 'lodash';
import posthog from 'posthog-js';
import React from 'react';

import { globalConfig } from '~src/context/config';

import { CategoryStateContext } from '../category/category-state';
import { GlobalStateContext, SessionStateContext, useGlobalState } from '../global-state';
import { WidgetTheme } from '../theme/theme';
import { useIpc } from '../utils/ipc-utils';

/**
 * Every component that is renderer in fullscreen should be exported in this
 * file.
 */
import * as ComponentIndex from './component-index';
import type { FullscreenComponent } from './fullscreen-mode';

export const FullscreenRenderer: React.FC = () => {
  const ipc = useIpc();
  const globalState = React.useContext(GlobalStateContext);

  React.useEffect(() => {
    if (!globalState) {
      return;
    }

    const { flags } = globalState.sessions[0];

    if (flags.enable_analytics) {
      posthog.init(globalConfig.posthogKey, {
        api_host: 'https://eu.posthog.com',
        autocapture: false,
        capture_pageview: false,
      });
    }
  }, [!!globalState]);

  const [componentArray, setComponentArray] = React.useState<FullscreenComponentDef[]>([]);

  React.useEffect(() => {
    return ipc.listen(payload => {
      if (payload.name === 'render_fullscreen_component') {
        setComponentArray(oldArray => {
          const prevComponent = oldArray.find(
            componentDef => componentDef.renderId === payload.data.renderId
          );
          if (prevComponent) {
            return oldArray.map(componentDef => {
              if (componentDef.renderId === payload.data.renderId) {
                return payload.data;
              } else {
                return componentDef;
              }
            });
          } else {
            return [...oldArray, payload.data];
          }
        });
      }
      if (payload.name === 'hide_fullscreen_component') {
        setComponentArray(oldArray =>
          oldArray.filter(({ renderId }) => renderId !== payload.data.renderId)
        );
      }
    });
  }, []);

  const elements = componentArray.map(componentDef => (
    <FullscreenElementDisplay key={componentDef.renderId} componentDef={componentDef} />
  ));
  return <>{elements}</>;
};

interface FullscreenElementDisplayProps {
  componentDef: FullscreenComponentDef;
}

const FullscreenElementDisplay: React.FC<FullscreenElementDisplayProps> = ({ componentDef }) => {
  const globalState = useGlobalState();
  const ipc = useIpc();

  const { renderId, componentName, staticProps, callbackPropsList, categoryId, sessionId } =
    componentDef;

  // @ts-ignore
  const componentDict = groupBy(
    ComponentIndex,
    (component: FullscreenComponent) => component.fullscreenName
  );

  if (!componentDict[componentName]) {
    throw `Component ${componentName} not imported in fullscreen.`;
  }

  const componentFn = componentDict[componentName][0];

  const callbackProps: Record<string, any> = {};

  // generate callback stubs
  callbackPropsList.forEach(propName => {
    callbackProps[propName] = (...args: any[]) => {
      ipc.send({
        name: 'run_fullscreen_callback',
        data: { renderId, propName, args },
      });
    };
  });

  const element = React.createElement(componentFn, {
    ...staticProps,
    ...callbackProps,
  });

  // note: sessionId depends on the context that triggered the route.
  const session = globalState.sessions.find(({ session_id }) => session_id === sessionId);
  const category = session ? session.categories.find(({ id }) => id === categoryId) : undefined;
  return (
    <SessionStateContext.Provider value={session}>
      <CategoryStateContext.Provider value={category}>
        <WidgetTheme>{element}</WidgetTheme>
      </CategoryStateContext.Provider>
    </SessionStateContext.Provider>
  );
};

interface FullscreenComponentDef {
  renderId: string;
  componentName: string;
  staticProps: Record<string, any>;
  callbackPropsList: string[];
  sessionId?: string;
  categoryId?: string;
}

export interface FullscreenRendererEvents {
  // Note: This is typed implicitly
  render_fullscreen_component: FullscreenComponentDef;
  hide_fullscreen_component: { renderId: string };
  run_fullscreen_callback: { renderId: string; propName: string; args: any[] };
}
